import React, { useCallback, useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { SCHOOL_NAV } from "./SchoolDashboard";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { ShieldCheck } from "lucide-react";

const APPROVAL_LEVELS = ["teacher", "department_head", "principal", "accounts"];
const LABEL = { teacher: "Teacher", department_head: "Dept Head", principal: "Principal", accounts: "Accounts" };

export default function Requisitions() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [f, setF] = useState({ title: "", department: "Academics", category: "Textbooks", justification: "", itemName: "", qty: 1, price: 0 });

  const load = useCallback(() => api.get("/requisitions").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/requisitions", {
        title: f.title, department: f.department, category: f.category, justification: f.justification,
        items: [{ name: f.itemName, qty: Number(f.qty), unit_price: Number(f.price) }],
      });
      toast.success("Requisition raised");
      setShowForm(false);
      setF({ title: "", department: "Academics", category: "Textbooks", justification: "", itemName: "", qty: 1, price: 0 });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const act = async (id, action) => {
    try {
      await api.post(`/requisitions/${id}/action`, { action, remarks: "" });
      toast.success(`Requisition ${action}d`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  // Can this user act at the current pending level on this requisition?
  const canAct = (r) => {
    if (r.status !== "pending_approval") return false;
    if (!user?.approval_role) return false;
    const expected = APPROVAL_LEVELS[r.approval_level];
    if (user.approval_role !== expected) return false;
    if (user.id === r.created_by) return false;
    if ((r.approvals || []).some((a) => a.by_id === user.id)) return false;
    return true;
  };

  return (
    <DashboardLayout nav={SCHOOL_NAV} title="Requisitions">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
        <div className="text-sm">
          <p className="text-slate-600">Segregated approvals: Teacher → Dept Head → Principal → Accounts. Different users are required at each level.</p>
          {user?.approval_role ? (
            <div className="mt-1 inline-flex items-center gap-2 text-xs bg-blue-50 text-brand-blue px-3 py-1 rounded-full font-semibold">
              <ShieldCheck size={14} /> You approve at: <span className="uppercase">{LABEL[user.approval_role]}</span>
            </div>
          ) : (
            <div className="mt-1 inline-flex items-center gap-2 text-xs bg-slate-100 text-slate-600 px-3 py-1 rounded-full">
              You do not have an approval role. Ask the principal to assign one.
            </div>
          )}
        </div>
        <button data-testid="new-req" onClick={() => setShowForm(!showForm)} className="rounded-md bg-brand-blue text-white font-semibold px-4 py-2 hover:bg-brand-blueDark transition-colors duration-200">
          {showForm ? "Cancel" : "Raise Requisition"}
        </button>
      </div>
      {showForm && (
        <form onSubmit={create} className="sl-card p-5 mb-6 grid md:grid-cols-2 gap-3">
          <input required placeholder="Requisition title" value={f.title} onChange={(e) => setF({...f, title: e.target.value})} data-testid="req-title" className="rounded-md border border-slate-300 px-3 py-2" />
          <input required placeholder="Department" value={f.department} onChange={(e) => setF({...f, department: e.target.value})} data-testid="req-dept" className="rounded-md border border-slate-300 px-3 py-2" />
          <select value={f.category} onChange={(e) => setF({...f, category: e.target.value})} data-testid="req-cat" className="rounded-md border border-slate-300 px-3 py-2">
            {["Textbooks","Library Books","Stationery","Uniforms","Shoes","Laboratory Equipment","Sports Equipment","Furniture","IT Equipment","STEM Kits"].map((c) => <option key={c}>{c}</option>)}
          </select>
          <input placeholder="Justification" value={f.justification} onChange={(e) => setF({...f, justification: e.target.value})} className="rounded-md border border-slate-300 px-3 py-2" />
          <input required placeholder="Item name" value={f.itemName} onChange={(e) => setF({...f, itemName: e.target.value})} data-testid="req-item" className="rounded-md border border-slate-300 px-3 py-2" />
          <div className="grid grid-cols-2 gap-3">
            <input type="number" min="1" value={f.qty} onChange={(e) => setF({...f, qty: e.target.value})} placeholder="Qty" data-testid="req-qty" className="rounded-md border border-slate-300 px-3 py-2" />
            <input type="number" min="0" value={f.price} onChange={(e) => setF({...f, price: e.target.value})} placeholder="Unit price" data-testid="req-price" className="rounded-md border border-slate-300 px-3 py-2" />
          </div>
          <button data-testid="req-submit" className="md:col-span-2 rounded-md bg-brand-blue text-white font-semibold py-2 hover:bg-brand-blueDark transition-colors duration-200">Submit</button>
        </form>
      )}

      <div className="space-y-4">
        {items.length === 0 && <div className="sl-card p-8 text-center text-slate-500">No requisitions yet.</div>}
        {items.map((r) => {
          const acted = canAct(r);
          const iAmCreator = user?.id === r.created_by;
          const expected = APPROVAL_LEVELS[r.approval_level];
          const alreadyActed = (r.approvals || []).some((a) => a.by_id === user?.id);
          return (
            <div key={r.id} className="sl-card p-5" data-testid={`req-row-${r.id}`}>
              <div className="flex flex-wrap justify-between items-start gap-3">
                <div>
                  <div className="font-heading font-semibold text-slate-900">{r.title}</div>
                  <div className="text-sm text-slate-600">{r.department} · {r.category} · Raised by {r.created_by_name}</div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-semibold text-slate-800">₹{Number(r.total).toLocaleString('en-IN')}</div>
                  <div className={`inline-block text-xs px-2 py-1 rounded-full mt-1 ${
                    r.status === "approved" ? "bg-emerald-50 text-brand-greenDark" :
                    r.status === "rejected" ? "bg-red-50 text-red-600" : "bg-amber-50 text-amber-700"
                  }`}>{r.status.replace("_", " ")}</div>
                </div>
              </div>
              <div className="mt-4 flex items-center gap-2">
                {APPROVAL_LEVELS.map((s, i) => (
                  <React.Fragment key={s}>
                    <div className={`text-xs px-2 py-1 rounded-full ${i < r.approval_level ? "bg-brand-blue text-white" : "bg-slate-100 text-slate-500"}`}>{LABEL[s]}</div>
                    {i < APPROVAL_LEVELS.length - 1 && <div className="flex-1 h-px bg-slate-200"></div>}
                  </React.Fragment>
                ))}
              </div>
              {r.status === "pending_approval" && (
                <div className="mt-4 flex items-center gap-2 flex-wrap">
                  {acted && (
                    <>
                      <button onClick={() => act(r.id, "approve")} data-testid={`req-approve-${r.id}`} className="rounded-md bg-brand-green text-white text-sm px-4 py-2 hover:bg-brand-greenDark transition-colors duration-200">Approve as {LABEL[user.approval_role]}</button>
                      <button onClick={() => act(r.id, "reject")} data-testid={`req-reject-${r.id}`} className="rounded-md border border-red-200 text-red-600 text-sm px-4 py-2 hover:bg-red-50 transition-colors duration-200">Reject</button>
                    </>
                  )}
                  {!acted && iAmCreator && <span className="text-xs text-slate-500">You raised this requisition — another approver must act.</span>}
                  {!acted && alreadyActed && <span className="text-xs text-slate-500">You have already acted on this requisition.</span>}
                  {!acted && !iAmCreator && !alreadyActed && (
                    <span className="text-xs text-slate-500">Waiting on <b className="uppercase">{LABEL[expected]}</b>.</span>
                  )}
                </div>
              )}
              {(r.approvals || []).length > 0 && (
                <div className="mt-3 border-t border-slate-100 pt-3 text-xs text-slate-500 space-y-1">
                  {(r.approvals || []).map((a) => (
                    <div key={`${a.by_id || a.by}-${a.at}-${a.level}`}>
                      <span className="uppercase font-semibold">{LABEL[a.level] || a.level}</span> — {a.action}d by {a.by}
                      {a.remarks ? ` · ${a.remarks}` : ""}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </DashboardLayout>
  );
}
